/** @format */

import {
  ProgramAttentionCategory,
  ProgramAttentionActionQuery,
  ProgramAttentionPreview,
  ProgramAttentionSendResult,
} from "@domain/program/program-attention-actions.model";
/** @format */

import { inject, Injectable } from "@angular/core";
import { ProgramRoleWidget } from "@domain/program/program-role-widget.model";
import { ApiService } from "@corelib";
import { Observable } from "rxjs";
import { HttpParams } from "@angular/common/http";
import {
  ProgramCaseQuery,
  ProgramCaseFilters,
  ProgramCaseSelection,
} from "@domain/program/program-case-analytics.model";
import { ApiPagination } from "@domain/other/api-pagination.model";
import { User } from "@domain/auth/user.model";
import { PartnerProgramFields } from "@domain/program/partner-program-fields.model";
import { Program, ProgramDataSchema } from "@domain/program/program.model";
import { ProgramCreate } from "@domain/program/program-create.model";
import { Project } from "@domain/project/project.model";
import { ProjectAdditionalFields } from "@domain/project/project-additional-fields.model";
import { ApplyToProgramDTO } from "@domain/program/dto/apply-to-program.model";
import { ApplyToProgramResponse } from "@domain/program/results/apply-to-program";
import {
  ProgramAnalyticsAttentionPage,
  ProgramAnalyticsAttentionParticipant,
  ProgramAnalyticsAttentionProjects,
  ProgramAnalyticsNotSubmittedProjectsPage,
  ProgramAnalyticsAttentionQuery,
} from "@domain/program/program-analytics-attention.model";
import {
  ProgramAnalyticsOverview,
  ProgramAnalyticsAssignment,
  ProgramAnalyticsAssignmentScope,
  ProgramAnalyticsAssignmentScoreDetail,
} from "@domain/program/program-analytics.model";

/** HTTP-адаптер программ: `/programs`, `/auth/public-users` (детали, проекты, участники, фильтры, регистрация). */
@Injectable({ providedIn: "root" })
export class ProgramHttpAdapter {
  previewAttentionReminder(
    programId: number,
    category: ProgramAttentionCategory,
    query: ProgramAttentionActionQuery,
  ): Observable<ProgramAttentionPreview> {
    return this.apiService.post(
      `/programs/${programId}/manager-overview/attention/${category}/reminder-preview/`,
      query,
    );
  }
  sendAttentionReminder(
    programId: number,
    category: ProgramAttentionCategory,
    token: string,
  ): Observable<ProgramAttentionSendResult> {
    return this.apiService.post(
      `/programs/${programId}/manager-overview/attention/${category}/reminders/`,
      { token },
    );
  }
  exportAttention(
    programId: number,
    category: ProgramAttentionCategory,
    query: ProgramAttentionActionQuery,
  ): Observable<Blob> {
    return this.apiService.postFile(
      `/programs/${programId}/manager-overview/attention/${category}/export/`,
      query,
    );
  }

  getCaseProjects(programId: number, query: ProgramCaseQuery): Observable<unknown> {
    const params = this.caseParams(query.selection, query)
      .set("limit", query.limit ?? 25)
      .set("offset", query.offset ?? 0);
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/projects/`, params);
  }

  exportCaseProjects(
    programId: number,
    selection: ProgramCaseSelection,
    filters: ProgramCaseFilters = {},
  ): Observable<Blob> {
    return this.apiService.getFile(
      `${this.PROGRAMS_URL}/${programId}/export-projects/`,
      this.caseParams(selection, filters),
    );
  }

  private caseParams(selection: ProgramCaseSelection, filters: ProgramCaseFilters): HttpParams {
    let params = new HttpParams().set("view", "case_analytics").set("case_scope", selection.scope);
    if (filters.search?.trim()) params = params.set("search", filters.search.trim());
    if (filters.submission && filters.submission !== "all")
      params = params.set("submission", filters.submission);
    if (filters.presentation && filters.presentation !== "all")
      params = params.set("presentation", filters.presentation);
    return selection.scope === "selected" ? params.set("case_name", selection.caseName) : params;
  }

  getRoleWidget(programId: number): Observable<ProgramRoleWidget> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/analytics-widget/`);
  }
  private readonly PROGRAMS_URL = "/programs";
  private readonly AUTH_PUBLIC_USERS_URL = "/auth/public-users";
  private readonly apiService = inject(ApiService);

  getAll(skip: number, take: number, params?: HttpParams): Observable<ApiPagination<Program>> {
    let httpParams = new HttpParams().set("limit", take).set("offset", skip);

    if (params) {
      params.keys().forEach(key => {
        const value = params.get(key);
        if (value !== null) {
          httpParams = httpParams.set(key, value);
        }
      });
    }

    return this.apiService.get(`${this.PROGRAMS_URL}/`, httpParams);
  }

  getOne(programId: number): Observable<Program> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/`);
  }

  getPublishedProjects(programId: number, params?: HttpParams): Observable<ApiPagination<Project>> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/published-projects/`, params);
  }

  getManagerOverview(programId: number): Observable<ProgramAnalyticsOverview> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/manager-overview/`);
  }

  /** Без автозагрузки остальных страниц; camelcase преобразует общий interceptor. */
  getManagerParticipantsWithoutVisit(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsAttentionPage<ProgramAnalyticsAttentionParticipant>> {
    return this.apiService.get(
      `${this.PROGRAMS_URL}/${programId}/manager-overview/participants-without-visit/`,
      this.attentionParams(query),
    );
  }

  /** Без автозагрузки остальных страниц; camelcase преобразует общий interceptor. */
  getManagerParticipantsWithoutTeam(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsAttentionPage<ProgramAnalyticsAttentionParticipant>> {
    return this.apiService.get(
      `${this.PROGRAMS_URL}/${programId}/manager-overview/participants-without-team/`,
      this.attentionParams(query),
    );
  }

  /** Список работ не подменяется assignments и не включает несданные проекты. */
  getManagerProjectsAwaitingEvaluation(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsAttentionProjects> {
    return this.apiService.get(
      `${this.PROGRAMS_URL}/${programId}/manager-overview/projects-awaiting-evaluation/`,
      this.attentionParams(query),
    );
  }

  /** Отдельный список несданных связей; camelcase выполняет общий interceptor. */
  getManagerProjectsNotSubmitted(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsNotSubmittedProjectsPage> {
    return this.apiService.get(
      `${this.PROGRAMS_URL}/${programId}/manager-overview/projects-not-submitted/`,
      this.attentionParams(query),
    );
  }

  private attentionParams(query: ProgramAnalyticsAttentionQuery): HttpParams {
    let params = new HttpParams().set("limit", query.limit ?? 25).set("offset", query.offset ?? 0);
    const search = query.search?.trim();
    if (search) params = params.set("search", search);
    return params;
  }

  /** Query scope передаётся backend без переопределения его статусной семантики. */
  getManagerAssignments(
    programId: number,
    scope: ProgramAnalyticsAssignmentScope,
  ): Observable<ProgramAnalyticsAssignment[]> {
    return this.apiService.get(
      `${this.PROGRAMS_URL}/${programId}/manager-overview/assignments/`,
      new HttpParams().set("scope", scope),
    );
  }

  /** Контракт проходит через общий CamelcaseInterceptor. */
  getManagerAssignmentScores(
    programId: number,
    assignmentId: number,
  ): Observable<ProgramAnalyticsAssignmentScoreDetail> {
    return this.apiService.get(
      `${this.PROGRAMS_URL}/${programId}/manager-overview/assignments/${assignmentId}/scores/`,
    );
  }

  acknowledgeWelcome(programId: number): Observable<{ welcomeAcknowledgedAt: string }> {
    return this.apiService.post(`${this.PROGRAMS_URL}/${programId}/acknowledge-welcome/`, {});
  }

  create(program: ProgramCreate): Observable<Program> {
    return this.apiService.post(`${this.PROGRAMS_URL}/`, program);
  }

  getDataSchema(programId: number): Observable<{ dataSchema: ProgramDataSchema }> {
    return this.apiService.get<{ dataSchema: ProgramDataSchema }>(
      `${this.PROGRAMS_URL}/${programId}/schema/`,
    );
  }

  register(
    programId: number,
    additionalData: Record<string, string>,
  ): Observable<ProgramDataSchema> {
    return this.apiService.post(`${this.PROGRAMS_URL}/${programId}/register/`, additionalData);
  }

  getAllProjects(programId: number, params?: HttpParams): Observable<ApiPagination<Project>> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/projects/`, params);
  }

  getAllMembers(programId: number, skip: number, take: number): Observable<ApiPagination<User>> {
    return this.apiService.get(
      `${this.AUTH_PUBLIC_USERS_URL}/`,
      new HttpParams({ fromObject: { partner_program: programId, limit: take, offset: skip } }),
    );
  }

  getProgramFilters(programId: number): Observable<PartnerProgramFields[]> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/filters/`);
  }

  getProgramProjectAdditionalFields(programId: number): Observable<ProjectAdditionalFields> {
    return this.apiService.get(`${this.PROGRAMS_URL}/${programId}/projects/apply/`);
  }

  applyProjectToProgram(
    programId: number,
    dto: ApplyToProgramDTO,
  ): Observable<ApplyToProgramResponse> {
    const payload = {
      project: dto.project,
      program_field_values: dto.programFieldValues.map(({ fieldId, valueText }) => ({
        field_id: fieldId,
        value_text: valueText,
      })),
    };
    return this.apiService.post(`${this.PROGRAMS_URL}/${programId}/projects/apply/`, payload);
  }

  createProgramFilters(
    programId: number,
    filters: Record<string, string[]>,
    params?: HttpParams,
  ): Observable<ApiPagination<Project>> {
    let url = `${this.PROGRAMS_URL}/${programId}/projects/filter/`;
    if (params) {
      url += `?${params.toString()}`;
    }
    return this.apiService.post(url, { filters });
  }

  submitCompettetiveProject(relationId: number): Observable<Project> {
    return this.apiService.post(
      `${this.PROGRAMS_URL}/partner-program-projects/${relationId}/submit/`,
      {},
    );
  }
}
